import { createApp } from "vue"
import App from "./App.vue"
import router from "./router"
import store from "./store"
import "./styles/main.scss"
import VueLazyload from "vue-lazyload"
import errorHandler from "@/services/errorHandler"
import { reportClientRuntimeError } from "@/services/clientErrorReporter"

const MAX_JS_ERRORS = 100

/**
 * Push an error entry into global diagnostics collector with fixed-size cap.
 * Both `error` and `unhandledrejection` handlers must use this helper.
 *
 * @param {object} errorInfo - normalized error payload
 */
function pushJsErrorBounded(errorInfo) {
  if (typeof window === "undefined") return

  if (!Array.isArray(window.js_errors)) {
    window.js_errors = []
  }

  if (window.js_errors.length >= MAX_JS_ERRORS) {
    window.js_errors.shift()
  }

  window.js_errors.push(errorInfo)
}

/**
 * Fire-and-forget client error reporting without letting reporter failures recurse through global handlers.
 * Existing js_errors capture and errorHandler behavior must run regardless of sink success.
 *
 * @param {Error|any} err - runtime error-like object
 * @param {object} context - sanitized reporter context
 */
function reportClientRuntimeErrorSafe(err, context) {
  void reportClientRuntimeError(err, context).catch(() => false)
}

// Global error collector for E2E/tests (bounded for long-lived tabs)
if (typeof window !== "undefined" && !Array.isArray(window.js_errors)) {
  window.js_errors = []
}

const app = createApp(App)

app.config.errorHandler = (err, vm, info) => {
  const errorInfo = {
    message: err.message,
    stack: err.stack,
    component: vm?.$options?.name || "unknown",
    info: info,
  }

  pushJsErrorBounded(errorInfo)
  errorHandler.handle(err)
  reportClientRuntimeErrorSafe(err, {
    component: vm?.$options?.name || "unknown",
    action: "vue-error-handler",
    info,
  })
}

window.addEventListener("error", (event) => {
  const errorInfo = {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    stack: event.error?.stack,
  }

  pushJsErrorBounded(errorInfo)
  reportClientRuntimeErrorSafe(
    event.error || new Error(event.message || "Window error"),
    {
      action: "window-error",
    }
  )
})

window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason
  const msg =
    reason?.message ?? (typeof reason === "string" ? reason : "Unknown")
  const errorInfo = { message: msg, stack: reason?.stack }

  pushJsErrorBounded(errorInfo)

  // Skip errorHandler for E2E infra rejections (C4A WAIT timeout) - not app errors
  if (typeof reason === "string" && reason.includes("WAIT selector timeout")) {
    return
  }

  if (reason?.__handledForbidden) {
    return
  }

  errorHandler.handle(reason)
  reportClientRuntimeErrorSafe(reason, {
    action: "unhandledrejection",
  })
})

app.use(store).use(router).use(VueLazyload, { preLoad: 0.9 }).mount("#app")
